/**
 * excelEngine.ts — Unified Excel abstraction layer for NetUno
 *
 * Replaces direct usage of xlsx (vulnerable) with ExcelJS.
 * All Excel I/O in the frontend MUST go through this module.
 *
 * Security features:
 * - File size limit (10 MB)
 * - Row limit (50,000)
 * - Column limit (200)
 * - No formula/macro execution — values only
 */

import ExcelJS from 'exceljs';

// ── Security Constants ──────────────────────────────────────
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_ROWS = 50_000;
const MAX_COLUMNS = 200;

export class ExcelSecurityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ExcelSecurityError';
  }
}

// ── File Validation ─────────────────────────────────────────

function validateFileSize(file: File | ArrayBuffer): void {
  const size = file instanceof File ? file.size : file.byteLength;
  if (size > MAX_FILE_SIZE_BYTES) {
    throw new ExcelSecurityError(
      `Arquivo excede o limite de ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB.`
    );
  }
}

function validateSheetLimits(worksheet: ExcelJS.Worksheet): void {
  const rowCount = worksheet.rowCount;
  const colCount = worksheet.columnCount;

  if (rowCount > MAX_ROWS) {
    throw new ExcelSecurityError(
      `Planilha excede o limite de ${MAX_ROWS.toLocaleString('pt-BR')} linhas (encontrado: ${rowCount.toLocaleString('pt-BR')}).`
    );
  }
  if (colCount > MAX_COLUMNS) {
    throw new ExcelSecurityError(
      `Planilha excede o limite de ${MAX_COLUMNS} colunas (encontrado: ${colCount}).`
    );
  }
}

/**
 * Sanitize a cell value: strip formulas, return plain value only.
 */
function sanitizeCell(cell: ExcelJS.Cell): unknown {
  if (cell.type === ExcelJS.ValueType.Formula) {
    // Return the cached result, never the formula
    return (cell.value as ExcelJS.CellFormulaValue)?.result ?? null;
  }
  if (cell.type === ExcelJS.ValueType.RichText) {
    const rt = cell.value as ExcelJS.CellRichTextValue;
    return rt.richText?.map(r => r.text).join('') ?? '';
  }
  if (cell.type === ExcelJS.ValueType.Hyperlink) {
    const hl = cell.value as ExcelJS.CellHyperlinkValue;
    return hl.text ?? hl.hyperlink ?? '';
  }
  if (cell.type === ExcelJS.ValueType.Error) {
    return null;
  }
  return cell.value;
}

function createEmptyRecord(headers: string[]): Record<string, unknown> {
  const record: Record<string, unknown> = {};
  for (const key of headers) {
    if (key) record[key] = undefined;
  }
  return record;
}

// ── READ: Parse Excel file → Array of objects ───────────────

export interface ReadOptions {
  /** Sheet name or index (0-based). Default: 0 */
  sheet?: string | number;
  /** Skip file-size validation (for internal trusted sources) */
  skipSizeCheck?: boolean;
}

/**
 * Read an Excel file and return rows as plain objects.
 * First row is treated as headers.
 */
export async function readExcel(
  source: File | ArrayBuffer,
  options: ReadOptions = {}
): Promise<{ sheetNames: string[]; rows: Record<string, unknown>[] }> {
  if (!options.skipSizeCheck) {
    validateFileSize(source);
  }

  const buffer =
    source instanceof File ? await source.arrayBuffer() : source;

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const sheetNames = workbook.worksheets.map(ws => ws.name);

  // Resolve target sheet
  let worksheet: ExcelJS.Worksheet | undefined;
  if (typeof options.sheet === 'string') {
    worksheet = workbook.worksheets.find(
      ws => ws.name.toLowerCase().includes(options.sheet!.toString().toLowerCase())
    );
  } else {
    const idx = options.sheet ?? 0;
    worksheet = workbook.worksheets[idx];
  }

  if (!worksheet) {
    return { sheetNames, rows: [] };
  }

  validateSheetLimits(worksheet);

  // Extract headers from first row
  const headerRow = worksheet.getRow(1);
  const headers: string[] = [];
  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    headers[colNumber] = String(sanitizeCell(cell) ?? `col_${colNumber}`);
  });

  // Extract data rows
  const rows: Record<string, unknown>[] = [];
  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) return; // skip header
    const record = createEmptyRecord(headers);
    let hasValue = false;
    row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      const key = headers[colNumber];
      if (key) {
        record[key] = sanitizeCell(cell);
        hasValue = true;
      }
    });
    if (hasValue) rows.push(record);
  });

  return { sheetNames, rows };
}

/**
 * Read all sheets from an Excel file.
 */
export async function readAllSheets(
  source: File | ArrayBuffer
): Promise<{ name: string; rows: Record<string, unknown>[] }[]> {
  validateFileSize(source);

  const buffer = source instanceof File ? await source.arrayBuffer() : source;
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const result: { name: string; rows: Record<string, unknown>[] }[] = [];

  for (const worksheet of workbook.worksheets) {
    validateSheetLimits(worksheet);

    const headerRow = worksheet.getRow(1);
    const headers: string[] = [];
    headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      headers[colNumber] = String(sanitizeCell(cell) ?? `col_${colNumber}`);
    });

    const rows: Record<string, unknown>[] = [];
    worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      if (rowNumber === 1) return;
      const record = createEmptyRecord(headers);
      let hasValue = false;
      row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
        const key = headers[colNumber];
        if (key) {
          record[key] = sanitizeCell(cell);
          hasValue = true;
        }
      });
      if (hasValue) rows.push(record);
    });

    result.push({ name: worksheet.name, rows });
  }

  return result;
}

/**
 * Find a sheet by partial name match (case-insensitive).
 */
export async function findSheet(
  source: File | ArrayBuffer,
  partialName: string
): Promise<{ sheetNames: string[]; rows: Record<string, unknown>[] }> {
  return readExcel(source, { sheet: partialName });
}

// ── WRITE: Export data → download xlsx ──────────────────────

export interface WriteSheetData {
  name: string;
  data: Record<string, unknown>[];
  /** Optional column widths override */
  columnWidths?: Record<string, number>;
}

/**
 * Write data to an Excel file and trigger browser download.
 */
export async function writeExcel(
  sheets: WriteSheetData[],
  filename: string
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'NetUno';
  workbook.created = new Date();

  for (const sheet of sheets) {
    if (sheet.data.length === 0) continue;

    const ws = workbook.addWorksheet(sheet.name.slice(0, 31));
    const keys = Object.keys(sheet.data[0]);

    // Add header row
    ws.addRow(keys);

    // Style header
    const headerRow = ws.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: 'middle' };

    // Add data rows
    for (const row of sheet.data) {
      ws.addRow(keys.map(k => row[k] ?? ''));
    }

    // Auto-size columns
    keys.forEach((key, idx) => {
      const col = ws.getColumn(idx + 1);
      const maxLen = Math.max(
        key.length,
        ...sheet.data.map(row => String(row[key] ?? '').length)
      );
      col.width = Math.min(
        sheet.columnWidths?.[key] ?? maxLen + 2,
        40
      );
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBuffer(buffer as ArrayBuffer, filename);
}

/**
 * Write a single sheet from array-of-arrays (for templates).
 */
export async function writeExcelAoa(
  sheets: Array<{ name: string; rows: unknown[][] }>,
  filename: string
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'NetUno';
  workbook.created = new Date();

  for (const sheet of sheets) {
    const ws = workbook.addWorksheet(sheet.name.slice(0, 31));
    for (const row of sheet.rows) {
      ws.addRow(row);
    }

    // Style first row as header
    if (sheet.rows.length > 0) {
      const headerRow = ws.getRow(1);
      headerRow.font = { bold: true };

      // Auto-size columns based on header
      const firstRow = sheet.rows[0] as unknown[];
      firstRow.forEach((_val, idx) => {
        const col = ws.getColumn(idx + 1);
        const maxLen = Math.max(
          ...sheet.rows.map(r => String((r as unknown[])[idx] ?? '').length)
        );
        col.width = Math.min(maxLen + 2, 40);
      });
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBuffer(buffer as ArrayBuffer, filename);
}

// ── Utility: trigger download in browser ────────────────────

function downloadBuffer(buffer: ArrayBuffer, filename: string): void {
  const safeName = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Convert buffer to Uint8Array (for Edge Functions or APIs that need raw bytes).
 */
export async function writeExcelToBuffer(
  sheets: WriteSheetData[]
): Promise<Uint8Array> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'NetUno';
  workbook.created = new Date();

  for (const sheet of sheets) {
    if (sheet.data.length === 0) continue;
    const ws = workbook.addWorksheet(sheet.name.slice(0, 31));
    const keys = Object.keys(sheet.data[0]);
    ws.addRow(keys);
    ws.getRow(1).font = { bold: true };
    for (const row of sheet.data) {
      ws.addRow(keys.map(k => row[k] ?? ''));
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  return new Uint8Array(buffer);
}
